import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { CheckCheck, Trash2, Loader2 } from '../../icons';
import {
  fetchNotifications, markAsRead, readAll, deleteNotification, type NotificationDto,
} from '../../api/notifications';
import { useNotificationStore } from '../../store/notificationStore';
import { notifMeta, notifCategoryLabel } from '../../lib/notification-meta';
import {
  tNotif, formatNotifTime, getNotifLang, subscribeNotifLang,
} from '../../lib/notification-i18n';
import './MyNotificationsTab.css';

type Filter = 'all' | 'unread';
const PAGE = 20;

/**
 * MyNotificationsTab (v2.13) — 내 프로필 '내 알림' 탭.
 * 전체/안읽음 필터 + 커서 페이지네이션('더 보기') + 항목 읽음/삭제 + 모두 읽음.
 * 카테고리 아이콘·색은 notifMeta SSOT(드롭다운과 공유).
 */
export function MyNotificationsTab() {
  useSyncExternalStore(subscribeNotifLang, getNotifLang, getNotifLang);
  const [filter, setFilter] = useState<Filter>('all');
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const setUnread = useNotificationStore((s) => s.setUnread);
  const decrementUnread = useNotificationStore((s) => s.decrementUnread);

  const load = useCallback(async (f: Filter, c: string | null, append: boolean) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res = await fetchNotifications({
        limit: PAGE,
        ...(f === 'unread' ? { isRead: false } : {}),
        ...(c ? { cursor: c } : {}),
      });
      setItems((prev) => (append ? [...prev, ...(res.notifications ?? [])] : (res.notifications ?? [])));
      setCursor(res.nextCursor ?? null);
    } catch {
      setError(tNotif('notif.error'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { void load(filter, null, false); }, [filter, load]);

  const onItemRead = useCallback(async (n: NotificationDto) => {
    if (n.isRead) return;
    try {
      const updated = await markAsRead(n.id);
      decrementUnread();
      setItems((prev) => {
        const next = prev.map((x) => (x.id === n.id ? updated : x));
        // 안읽음 필터에선 읽은 항목 제거.
        return filter === 'unread' ? next.filter((x) => !x.isRead) : next;
      });
    } catch { /* silent */ }
  }, [decrementUnread, filter]);

  const onItemDelete = useCallback(async (n: NotificationDto, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteNotification(n.id);
      if (!n.isRead) decrementUnread();
      setItems((prev) => prev.filter((x) => x.id !== n.id));
    } catch { /* silent */ }
  }, [decrementUnread]);

  const onReadAll = useCallback(async () => {
    try {
      await readAll();
      setUnread(0);
      setItems((prev) => (filter === 'unread' ? [] : prev.map((x) => ({ ...x, isRead: true }))));
    } catch { /* silent */ }
  }, [setUnread, filter]);

  const allRead = items.every((n) => n.isRead);

  return (
    <section className="settings-section mynotif" aria-label={tNotif('notif.tab.heading')}>
      <div className="mynotif-head">
        <div>
          <h2 className="mynotif-title">{tNotif('notif.tab.heading')}</h2>
          <p className="mynotif-subtitle">{tNotif('notif.tab.subtitle')}</p>
        </div>
        <button type="button" className="mynotif-readall" onClick={() => void onReadAll()} disabled={allRead && filter !== 'unread'}>
          <CheckCheck size={14} aria-hidden /> {tNotif('notif.read_all')}
        </button>
      </div>

      <div className="mynotif-filters" role="tablist" aria-label={tNotif('notif.tab.heading')}>
        {(['all', 'unread'] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={filter === f}
            className={`mynotif-filter${filter === f ? ' is-active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {tNotif(f === 'all' ? 'notif.filter.all' : 'notif.filter.unread')}
          </button>
        ))}
      </div>

      {error && <div className="mynotif-status mynotif-error" role="alert">{error}</div>}
      {loading && (
        <div className="mynotif-list">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="mynotif-skeleton" style={{ animationDelay: `${i * 70}ms` }}>
              <span className="mynotif-skeleton-ico" />
              <span className="mynotif-skeleton-lines"><span /><span /></span>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="mynotif-empty" role="status">
          <span className="mynotif-empty-ico" aria-hidden>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
          </span>
          {tNotif(filter === 'unread' ? 'notif.empty_unread' : 'notif.empty')}
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <ul className="mynotif-list">
          {items.map((n, i) => {
            const meta = notifMeta(n.type, n.source);
            const Icon = meta.Icon;
            return (
              <li key={n.id} style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}>
                <div
                  className={`mynotif-item${n.isRead ? '' : ' mynotif-item--unread'}`}
                  style={{ '--n-accent': meta.accent } as CSSProperties}
                  role="button"
                  tabIndex={0}
                  onClick={() => void onItemRead(n)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); void onItemRead(n); } }}
                  aria-label={tNotif('notif.aria_item', { title: n.title, unread: n.isRead ? '' : tNotif('notif.aria_item_unread') })}
                >
                  <span className="mynotif-ico" aria-hidden><Icon size={17} /></span>
                  <span className="mynotif-body">
                    <span className="mynotif-row1">
                      <span className="mynotif-cat">{notifCategoryLabel(n.type, n.source)}</span>
                      {!n.isRead && <span className="mynotif-dot" aria-hidden />}
                      <span className="mynotif-time">{formatNotifTime(n.createdAt)}</span>
                    </span>
                    <span className="mynotif-text">{n.title}</span>
                  </span>
                  <button
                    type="button"
                    className="mynotif-del"
                    aria-label={tNotif('notif.delete')}
                    onClick={(e) => void onItemDelete(n, e)}
                  >
                    <Trash2 size={14} aria-hidden />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && cursor && (
        <button type="button" className="mynotif-more" onClick={() => void load(filter, cursor, true)} disabled={loadingMore}>
          {loadingMore ? <Loader2 size={14} className="mynotif-spin" aria-hidden /> : tNotif('notif.more')}
        </button>
      )}
    </section>
  );
}
